import React, { useState } from "react";
import {
  View,
  StyleSheet,
  Text,
  TouchableOpacity,
  Clipboard,
  TextInput,
} from "react-native";
import { SessionContext } from "../../../../contexts";
import { postMessages } from "../../../../services/api-clinic";

type MessageThreadReplyProps = {
  patientUuid: string;
  aiResponse: string;
  fetchLatestMessageThread: (
    patientUuid: string,
    conversationId: number,
  ) => void;
};

const MessageThreadReply: React.FC<MessageThreadReplyProps> = ({
  patientUuid,
  aiResponse,
  fetchLatestMessageThread,
}) => {
  const session = React.useContext(SessionContext)?.session;

  const [checkedInput, setCheckedInput] = useState<boolean>(true);
  const [copyText, setCopyText] = useState("Copy");
  const [message, setMessage] = useState("");

  const handleCopy = () => {
    Clipboard.setString(aiResponse);
    setCopyText("Copied");
    setTimeout(() => setCopyText("Copy"), 1000);
  };

  const handleOnPressSend = async () => {
    try {
      if (!session) return;
      if (message.trim()) {
        setMessage("");
      }
      const query = {
        patient_uuid: patientUuid,
      };
      const body = {
        message: message,
        sender_type: "CLINIC",
      };
      await postMessages(session, query, body);
      fetchLatestMessageThread(patientUuid, -1);
    } catch (error) {}
  };

  return (
    <View style={[styles.container, styles.shadowBox]}>
      {checkedInput && (
        <View style={styles.aiResponseContainer}>
          <Text style={styles.aiResponseText}>{aiResponse}</Text>
          <View style={styles.copyButtonContainer}>
            <TouchableOpacity onPress={handleCopy} style={styles.copyButton}>
              <Text style={styles.copyButtonText}>{copyText}</Text>
            </TouchableOpacity>
          </View>
        </View>
      )}
      <View style={styles.inputContainer}>
        <TextInput
          value={message}
          placeholder="Type a response..."
          onChangeText={(text) => setMessage(text)}
          style={styles.textInput}
          multiline
          onSubmitEditing={handleOnPressSend}
        />
        <TouchableOpacity
          onPress={handleOnPressSend}
          disabled={!message.trim()}
        >
          <Text
            style={[
              styles.sendButtonText,
              !message.trim() && styles.disabledSendButton,
            ]}
          >
            Send
          </Text>
        </TouchableOpacity>
      </View>

      <View style={styles.checkboxContainer}>
        <input
          type="checkbox"
          checked={checkedInput}
          onChange={() => setCheckedInput((prev) => !prev)}
          style={{ accentColor: "#1677FF" }}
        />
        <Text style={styles.checkboxLabel}>AI Suggested Replies</Text>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    borderWidth: 1,
    borderColor: "#C4C6CD",
    borderRadius: 10,
    padding: 10,
    gap: 10,
  },
  shadowBox: {
    backgroundColor: "#fff",
    borderRadius: 10,
    elevation: 4,
    shadowColor: "rgba(0, 0, 0, 0.10)",
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 1,
    shadowRadius: 16,
  },
  aiResponseContainer: {
    flexDirection: "row",
    backgroundColor: "rgba(0, 183, 244, 0.10)",
    borderRadius: 8,
  },
  aiResponseText: {
    marginVertical: "auto",
    marginRight: "auto",
    fontFamily: "Urbanist400",
    fontSize: 14,
    padding: 10,
  },
  copyButtonContainer: {
    padding: 10,
    borderLeftWidth: 1,
    borderColor: "#C4C6CD",
  },
  copyButton: {
    backgroundColor: "white",
    borderRadius: 5,
    borderWidth: 1,
    borderColor: "#C4C6CD",
    padding: 5,
    marginVertical: "auto",
    alignItems: "center",
    width: 55,
  },
  copyButtonText: {
    fontFamily: "Urbanist600",
    fontSize: 14,
  },
  inputContainer: {
    flexDirection: "row",
    alignItems: "center",
    gap: 10,
  },
  textInput: {
    fontFamily: "Urbanist400",
    fontSize: 14,
    flex: 1,
    padding: 10,
    minHeight: 60,
  },
  sendButtonText: {
    fontFamily: "Urbanist600",
    color: "#515561",
    fontSize: 20,
    marginVertical: "auto",
    marginHorizontal: 10,
  },
  disabledSendButton: {
    color: "#D1D5DB", // Lighter gray color when disabled
  },
  checkboxContainer: {
    gap: 10,
    flexDirection: "row",
    alignItems: "center",
  },
  checkboxLabel: {
    fontFamily: "Urbanist600",
    color: "#515561",
    fontSize: 14,
  },
});

export default MessageThreadReply;
